import React, { useState, useEffect, useRef } from "react";
import PropTypes from "prop-types";

const AUTOCOMPLETION_URL = "https://autocomplete.search.hereapi.com/v1/autocomplete";
const API_KEY = "QJF2sIKVcgIx_0Au3uj74g262vyhqKaE7UwbXSC9jUk";

const getCountryCode = (countryName) => {
  const countryMap = {
    "South Africa": "ZAF",
    Namibia: "NAM",
    Cyprus: "CYP",
    Seychelles: "SYC",
  };
  return countryMap[countryName] || countryName;
};

const OMSTwitterGeoAutocomplete = ({ id = "", name = "", value = null, style = null, country = "South Africa", onChange = undefined }) => {
  const [inputValue, setInputValue] = useState("");
  const [suggestions, setSuggestions] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [locationId, setLocationId] = useState("");
  const [showDropdown, setShowDropdown] = useState(false);
  const inputRef = useRef(null);
  const dropdownRef = useRef(null);

  useEffect(() => {
    if (value) {
      setInputValue(value);
    }
  }, [value]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target) && inputRef.current && !inputRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const loadOptions = async (inputValue) => {
    if (!inputValue) {
      setSuggestions([]);
      return;
    }

    setIsLoading(true);
    try {
      const countryCode = getCountryCode(country);
      const response = await fetch(`${AUTOCOMPLETION_URL}?q=${encodeURIComponent(inputValue)}&apiKey=${API_KEY}&in=countryCode:${countryCode}`);
      const data = await response.json();

      if (!data.items) {
        setSuggestions([]);
        return;
      }

      const formattedSuggestions = data.items
        .map((item) => {
          if (!item) return null;
          const arr = item.title.split(",");
          arr.reverse();
          for (let j = 0; j < arr.length; j++) {
            arr[j] = arr[j].trim();
          }
          return {
            id: item.id,
            displayText: arr.join(", "),
            ...item,
          };
        })
        .filter(Boolean);

      setSuggestions(formattedSuggestions);
      setShowDropdown(true);
    } catch (error) {
      console.error("Error loading options:", error);
      setSuggestions([]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const value = e.target.value;
    setInputValue(value);
    loadOptions(value);
  };

  const handleSelect = (suggestion) => {
    if (locationId !== suggestion.id) {
      setInputValue(suggestion.displayText);
      setLocationId(suggestion.id);
      setShowDropdown(false);
      if (onChange) onChange(suggestion.id);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && suggestions.length > 0) {
      handleSelect(suggestions[0]);
    }
    if (e.key === "Escape") {
      setShowDropdown(false);
    }
  };

  return (
    <div className="dropdown" style={{ position: "relative" }}>
      <input type="hidden" data-id={id} id="hdnGeoAutocompleteC" name="hdnGeoAutocompleteC" value={country} />
      <input
        ref={inputRef}
        type="text"
        id={id}
        name={name}
        value={inputValue}
        onChange={handleInputChange}
        onKeyDown={handleKeyDown}
        onFocus={() => setShowDropdown(true)}
        placeholder="Please identify the listing on the map, start entering the address"
        className="form-control"
        autoComplete="off"
      />
      {showDropdown && (suggestions.length > 0 || isLoading) && (
        <div
          ref={dropdownRef}
          className="dropdown-menu"
          style={{
            position: "absolute",
            top: "100%",
            left: 0,
            right: 0,
            zIndex: 1000,
            maxHeight: "300px",
            overflowY: "auto",
            backgroundColor: "white",
            border: "1px solid #ccc",
            borderRadius: "4px",
            boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
            display: "block",
          }}
        >
          {isLoading ? (
            <div className="dropdown-item" style={{ padding: "8px 12px", color: "#666" }}>
              Loading locations...
            </div>
          ) : suggestions.length === 0 ? (
            <div className="dropdown-item" style={{ padding: "8px 12px", color: "#666" }}>
              No locations found
            </div>
          ) : (
            suggestions.map((suggestion) => {
              const arr = suggestion.displayText.split(",");
              return (
                <div
                  key={suggestion.id}
                  className="dropdown-item"
                  onClick={() => handleSelect(suggestion)}
                  style={{
                    padding: "8px 12px",
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "center",
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = "#f0f0f0";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = "white";
                  }}
                >
                  <i className="fa fa-map-marker" aria-hidden="true" style={{ marginRight: "8px" }}></i>
                  <span>
                    <strong>{arr[0]}</strong>
                    <small>,{arr.slice(1).join(",")}</small>
                  </span>
                </div>
              );
            })
          )}
        </div>
      )}
    </div>
  );
};

OMSTwitterGeoAutocomplete.propTypes = {
  id: PropTypes.string,
  name: PropTypes.string,
  value: PropTypes.string,
  style: PropTypes.object,
  country: PropTypes.string,
  onChange: PropTypes.func,
};

OMSTwitterGeoAutocomplete.defaultProps = {
  id: "",
  name: "",
  country: "South Africa",
  value: null,
  style: null,
  onChange: undefined,
};

export { OMSTwitterGeoAutocomplete };
